import { useRef } from "react";

/**
 * Makes a user's column resize stick across re-renders (HRMS-248).
 *
 * MUI X rebuilds its whole column state whenever the `columns` prop gets a new array
 * identity — it compares by reference — and the width declared in the colDef wins over
 * the width the user dragged to. Since our grids build `columns` inline in the render
 * body, any re-render silently snaps every resized column back to its designed width.
 *
 * This records each finished resize and feeds it back into the colDefs, so the width
 * survives re-renders from row clicks, edits, deletes and data refreshes alike. It also
 * survives the grid being unmounted and remounted (e.g. a refetch that blanks the rows),
 * which memoising `columns` cannot: a remounted grid re-reads the declared widths.
 *
 * Widths live for as long as the component stays mounted — navigating away resets them.
 *
 * @param {Array} columns  colDefs built by the caller (a fresh array each render is fine)
 * @returns {{columns: Array, onColumnWidthChange: Function}} spread straight onto <DataGrid>
 * @since NEXT_VERSION
 */
export const useResizableColumnWidths = (columns) => {
    // A ref, not state: recording a drag must not re-render, it only has to survive one.
    const resizedWidths = useRef({});

    const sizedColumns = columns.map((column) =>
        resizedWidths.current[column.field]
            ? { ...column, width: resizedWidths.current[column.field] }
            : column
    );

    const onColumnWidthChange = (params) => {
        resizedWidths.current[params.colDef.field] = params.width;
    };

    return { columns: sizedColumns, onColumnWidthChange };
};

export default useResizableColumnWidths;
